import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, Container, UncontrolledTooltip } from "reactstrap";
import { CONTACTS_IMPORT_LOADING } from "../../constants/loading.constant";
import {
  CONTACT_IMPORT_EXPORT,
  CONTACT_TAGS_FORM,
} from "../../constants/modal.constant";
import { CONTACT_TAGS } from "../../constants/internalModal.constant";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import ContactSideModals from "../component/contacts/ContactSideModals";
import ContactTags from "../component/contacts/modal/ContactTags";
import ImportContacts from "../component/contacts/modal/ImportContacts";
import { ContactsStep } from "../component/messageScheduling/ContactsStep";
import { MessageComposerStep } from "../component/messageScheduling/MessageComposerStep";
import { ScheduleStep } from "../component/messageScheduling/ScheduleStep";
import { ScheduledMessageList } from "../component/messageScheduling/ScheduledMessageList";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import {
  MessageSchedulingChannel,
  MessageSchedulingTemplate,
  ScheduledMessage,
  ScheduledMessageContact,
  ScheduledMessageLink,
  ScheduledMessageMedia,
  deleteMessageTemplate,
  deleteScheduledMessage,
  listMessageTemplates,
  listScheduledMessages,
  messageSchedulingId,
  saveMessageTemplate,
  saveScheduledMessage,
} from "../utils/messageSchedulingStorage";
import "./MessageScheduling.scss";

type Step = "channel" | "contacts" | "message" | "schedule";

const STEPS: Step[] = ["channel", "contacts", "message", "schedule"];

const CHANNELS: MessageSchedulingChannel[] = [
  "whatsapp",
  "facebook",
  "instagram",
];

const COMING_SOON_CHANNELS: MessageSchedulingChannel[] = [
  "facebook",
  "instagram",
];

const CHANNEL_ICON: Record<MessageSchedulingChannel, string> = {
  whatsapp: "whatsapp",
  facebook: "facebook",
  instagram: "instagram",
};

function defaultScheduledAt(): string {
  return moment().add(1, "hour").second(0).millisecond(0).toISOString();
}

export default function MessageScheduling() {
  const intl = useIntl();
  const { company, companyPlan, companyRole, isAdmin, isReseller } = useMeCore();
  const { goToSocial } = useRouterCore();
  const {
    isModal,
    turnOffModal,
    getModalVisibility,
    isLoading,
    turnOffInternalModal,
  } = useGlobalCore();

  const [view, setView] = useState<"list" | "create">("list");
  const [step, setStep] = useState<Step>("channel");
  const [items, setItems] = useState<ScheduledMessage[]>([]);
  const [templates, setTemplates] = useState<MessageSchedulingTemplate[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [channel, setChannel] = useState<MessageSchedulingChannel>("whatsapp");
  const [selectedContacts, setSelectedContacts] = useState<
    ScheduledMessageContact[]
  >([]);
  const [messageText, setMessageText] = useState("");
  const [media, setMedia] = useState<ScheduledMessageMedia[]>([]);
  const [link, setLink] = useState<ScheduledMessageLink | undefined>(undefined);
  const [scheduledAt, setScheduledAt] = useState(defaultScheduledAt());

  useEffect(() => {
    if (
      !companyPlan?.rules.allowWhatsappChat ||
      (!companyRole?.chat &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    ) {
      goToSocial();
    }
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    if (!company?.id) return;
    setItems(listScheduledMessages(company.id));
    setTemplates(listMessageTemplates(company.id));
  }, [company?.id]);

  const stepIndex = STEPS.indexOf(step);

  const resetForm = useCallback(() => {
    setStep("channel");
    setChannel("whatsapp");
    setSelectedContacts([]);
    setMessageText("");
    setMedia([]);
    setLink(undefined);
    setScheduledAt(defaultScheduledAt());
    setEditingId(null);
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setView("create");
  }, [resetForm]);

  const loadIntoWizard = useCallback(
    (item: ScheduledMessage, mode: "edit" | "duplicate") => {
      setChannel(item.channel);
      setSelectedContacts(item.contacts);
      setMessageText(item.text);
      setMedia(item.media);
      setLink(item.link);
      setScheduledAt(
        mode === "edit" && moment(item.scheduledAt).isAfter(moment())
          ? item.scheduledAt
          : defaultScheduledAt()
      );
      setEditingId(mode === "edit" ? item.id : null);
      setStep(mode === "edit" ? "schedule" : "message");
      setView("create");
    },
    []
  );

  const backToList = useCallback(() => {
    setView("list");
    resetForm();
  }, [resetForm]);

  const onChannelChange = useCallback((next: MessageSchedulingChannel) => {
    setChannel(next);
    if (next === "whatsapp") {
      setSelectedContacts((prev) =>
        prev.filter((contact) => Boolean(contact.phone))
      );
    }
  }, []);

  const hasMessage = useMemo(
    () => Boolean(messageText.trim()) || media.length > 0 || Boolean(link?.url),
    [link, media.length, messageText]
  );

  const canGoNext = useMemo(() => {
    if (step === "channel") return Boolean(channel);
    if (step === "contacts") return selectedContacts.length > 0;
    if (step === "message") return hasMessage;
    if (step === "schedule") {
      return Boolean(scheduledAt) && moment(scheduledAt).isAfter(moment());
    }
    return false;
  }, [channel, hasMessage, scheduledAt, selectedContacts.length, step]);

  const goNext = useCallback(() => {
    if (!canGoNext) {
      if (step === "contacts") {
        toast.error(
          intl.formatMessage({ id: "app.messageScheduling.contacts.none" })
        );
      } else if (step === "message") {
        toast.error(
          intl.formatMessage({ id: "app.messageScheduling.message.required" })
        );
      } else if (step === "schedule") {
        toast.error(
          intl.formatMessage({ id: "app.messageScheduling.schedule.past" })
        );
      }
      return;
    }
    const next = STEPS[stepIndex + 1];
    if (next) setStep(next);
  }, [canGoNext, intl, step, stepIndex]);

  const goBack = useCallback(() => {
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  }, [stepIndex]);

  const onSave = useCallback(() => {
    if (!company?.id) return;
    if (!selectedContacts.length) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.contacts.none" })
      );
      setStep("contacts");
      return;
    }
    if (!hasMessage) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.message.required" })
      );
      setStep("message");
      return;
    }
    if (!scheduledAt || !moment(scheduledAt).isAfter(moment())) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.schedule.past" })
      );
      setStep("schedule");
      return;
    }

    const existing = editingId
      ? items.find((item) => item.id === editingId)
      : undefined;

    const item: ScheduledMessage = {
      id: existing?.id || messageSchedulingId(),
      companyId: company.id,
      channel,
      contacts: selectedContacts,
      text: messageText.trim(),
      media,
      link: link?.url ? link : undefined,
      scheduledAt: moment(scheduledAt).toISOString(),
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: existing?.status || "scheduled",
    };

    setItems(saveScheduledMessage(item));
    toast.success(
      intl.formatMessage({
        id: editingId
          ? "app.messageScheduling.updated"
          : "app.messageScheduling.saved",
      })
    );
    backToList();
  }, [
    backToList,
    channel,
    company?.id,
    editingId,
    hasMessage,
    intl,
    items,
    link,
    media,
    messageText,
    scheduledAt,
    selectedContacts,
  ]);

  const onDelete = useCallback(
    (id: string) => {
      if (!company?.id) return;
      setItems(deleteScheduledMessage(company.id, id));
      toast.success(intl.formatMessage({ id: "app.messageScheduling.deleted" }));
    },
    [company?.id, intl]
  );

  const onSaveTemplate = useCallback(
    (template: MessageSchedulingTemplate) => {
      setTemplates(saveMessageTemplate(template));
      toast.success(
        intl.formatMessage({ id: "app.messageScheduling.templates.saved" })
      );
    },
    [intl]
  );

  const onDeleteTemplate = useCallback(
    (id: string) => {
      if (!company?.id) return;
      setTemplates(deleteMessageTemplate(company.id, id));
    },
    [company?.id]
  );

  const channelLabel = useCallback(
    (value: MessageSchedulingChannel) =>
      intl.formatMessage({ id: `app.messageScheduling.channel.${value}` }),
    [intl]
  );

  const previewContact = selectedContacts[0];

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.messageScheduling.title" })}
        rightChildren={
          view === "list" && items.length > 0 ? (
            <Button className="ms-btn ms-btn--primary" onClick={openCreate}>
              <FontAwesomeIcon icon={["fas", "plus"]} className="mr-2" />
              {intl.formatMessage({ id: "app.messageScheduling.create" })}
            </Button>
          ) : null
        }
      />
      <Content>
        <Container fluid className="ms-page">
          {view === "list" ? (
            <div className="ms-shell ms-shell--list">
              <div className="ms-hero">
                <span className="ms-hero__eyebrow">
                  <FontAwesomeIcon icon={["fas", "bolt"]} />
                  {intl.formatMessage({
                    id: "app.messageScheduling.hero.eyebrow",
                  })}
                </span>
                <h4 className="ms-hero__title">
                  {intl.formatMessage({ id: "app.messageScheduling.listTitle" })}
                </h4>
                <p className="ms-hero__text">
                  {intl.formatMessage({ id: "app.messageScheduling.subtitle" })}
                </p>
              </div>

              <ScheduledMessageList
                items={items}
                onCreate={openCreate}
                onEdit={(item) => loadIntoWizard(item, "edit")}
                onDuplicate={(item) => loadIntoWizard(item, "duplicate")}
                onDelete={onDelete}
              />
            </div>
          ) : (
            <div className="ms-shell ms-shell--wizard">
              <div className="ms-wizardTop">
                <button type="button" className="ms-linkish" onClick={backToList}>
                  <FontAwesomeIcon icon={["fas", "arrow-left"]} />
                  {intl.formatMessage({
                    id: "app.messageScheduling.backToList",
                  })}
                </button>
                {editingId && (
                  <span className="ms-badge ms-badge--editing">
                    <FontAwesomeIcon icon={["fas", "pen"]} className="mr-1" />
                    {intl.formatMessage({
                      id: "app.messageScheduling.editingBadge",
                    })}
                  </span>
                )}
              </div>

              <div className="ms-stepper">
                {STEPS.map((item, index) => {
                  const reachable =
                    index <= stepIndex || Boolean(editingId) || hasMessage;
                  return (
                    <button
                      key={item}
                      type="button"
                      className={`ms-step ${
                        index === stepIndex ? "is-active" : ""
                      } ${index < stepIndex ? "is-done" : ""}`}
                      onClick={() => {
                        if (reachable) setStep(item);
                      }}
                    >
                      <span className="ms-step__circle">
                        {index < stepIndex ? (
                          <FontAwesomeIcon icon={["fas", "check"]} />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <span className="ms-step__label">
                        {intl.formatMessage({
                          id: `app.messageScheduling.step.${item}`,
                        })}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="ms-panel">
                {step === "channel" && (
                  <>
                    <p className="ms-panel__hint">
                      {intl.formatMessage({
                        id: "app.messageScheduling.channel.hint",
                      })}
                    </p>
                    <div className="ms-channels">
                      {CHANNELS.map((option) => {
                        const comingSoon = COMING_SOON_CHANNELS.includes(option);
                        const channelId = `ms-channel-${option}`;
                        return (
                          <div
                            key={option}
                            id={channelId}
                            className={`ms-channelWrap ${
                              comingSoon ? "is-comingSoon" : ""
                            }`}
                          >
                            <button
                              type="button"
                              disabled={comingSoon}
                              className={`ms-channel ms-channel--${option} ${
                                channel === option ? "is-selected" : ""
                              } ${comingSoon ? "is-comingSoon" : ""}`}
                              onClick={() => {
                                if (!comingSoon) onChannelChange(option);
                              }}
                            >
                              <span className="ms-channel__top">
                                <span className="ms-channel__icon">
                                  <FontAwesomeIcon
                                    icon={["fab", CHANNEL_ICON[option] as any]}
                                    size="lg"
                                  />
                                </span>
                                {comingSoon && (
                                  <span className="ms-channel__badge">
                                    {intl.formatMessage({
                                      id: "app.messageScheduling.channel.comingSoon",
                                    })}
                                  </span>
                                )}
                              </span>
                              <span className="ms-channel__name">
                                {channelLabel(option)}
                              </span>
                              <span className="ms-channel__hint">
                                {intl.formatMessage({
                                  id: comingSoon
                                    ? "app.messageScheduling.channel.comingSoon.hint"
                                    : `app.messageScheduling.channel.${option}.hint`,
                                })}
                              </span>
                            </button>
                            {comingSoon && (
                              <UncontrolledTooltip
                                target={channelId}
                                placement="top"
                              >
                                {intl.formatMessage({
                                  id: "app.messageScheduling.channel.comingSoon.tooltip",
                                })}
                              </UncontrolledTooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {step === "contacts" && company?.id && (
                  <ContactsStep
                    companyId={company.id}
                    channel={channel}
                    selected={selectedContacts}
                    onChange={setSelectedContacts}
                  />
                )}

                {step === "message" && company?.id && (
                  <MessageComposerStep
                    companyId={company.id}
                    channel={channel}
                    text={messageText}
                    onTextChange={setMessageText}
                    media={media}
                    onMediaChange={setMedia}
                    link={link}
                    onLinkChange={setLink}
                    templates={templates}
                    onSaveTemplate={onSaveTemplate}
                    onDeleteTemplate={onDeleteTemplate}
                    previewContactName={previewContact?.name}
                    previewContactPhone={previewContact?.phone}
                    scheduledAt={scheduledAt}
                  />
                )}

                {step === "schedule" && (
                  <ScheduleStep
                    scheduledAt={scheduledAt}
                    onScheduledAtChange={setScheduledAt}
                    channel={channel}
                    contacts={selectedContacts}
                    text={messageText}
                    media={media}
                    link={link}
                    onEditChannel={() => setStep("channel")}
                    onEditContacts={() => setStep("contacts")}
                    onEditMessage={() => setStep("message")}
                  />
                )}
              </div>

              <div className="ms-wizardActions">
                <div>
                  {stepIndex > 0 && (
                    <Button className="ms-btn ms-btn--ghost" onClick={goBack}>
                      {intl.formatMessage({ id: "app.messageScheduling.back" })}
                    </Button>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="ms-linkish mr-3"
                    onClick={backToList}
                  >
                    {intl.formatMessage({ id: "app.messageScheduling.cancel" })}
                  </button>
                  {stepIndex < STEPS.length - 1 ? (
                    <Button className="ms-btn ms-btn--primary" onClick={goNext}>
                      {intl.formatMessage({ id: "app.messageScheduling.next" })}
                      <FontAwesomeIcon
                        icon={["fas", "arrow-right"]}
                        className="ml-2"
                      />
                    </Button>
                  ) : (
                    <Button className="ms-btn ms-btn--primary" onClick={onSave}>
                      <FontAwesomeIcon
                        icon={["fas", "paper-plane"]}
                        className="mr-2"
                      />
                      {intl.formatMessage({
                        id: editingId
                          ? "app.messageScheduling.saveChanges"
                          : "app.messageScheduling.save",
                      })}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Container>
      </Content>
      <ContactSideModals />
      <SideModal
        active={isModal(CONTACT_IMPORT_EXPORT)}
        visible={getModalVisibility(CONTACT_IMPORT_EXPORT)}
        onClose={() => turnOffModal(CONTACT_IMPORT_EXPORT)}
        showSppiner={isLoading(CONTACTS_IMPORT_LOADING)}
        sppinerMessage={intl.formatMessage({
          id: "app.messageScheduling.contacts.importing",
        })}
      >
        <ImportContacts />
      </SideModal>
      <SideModal
        active={isModal(CONTACT_TAGS_FORM)}
        visible={getModalVisibility(CONTACT_TAGS_FORM)}
        onClose={() => {
          turnOffModal(CONTACT_TAGS_FORM);
          turnOffInternalModal(CONTACT_TAGS);
        }}
      >
        <ContactTags />
      </SideModal>
    </Layout>
  );
}
